import { Text } from "@vericus/cadmus-ui";

import clsx from "clsx";

import { AnswerBlockFragment, QuestionType } from "@/generated/graphql";

import { StatusTag } from "../status-tag";
import * as styles from "./answer-sidebar.css";
import arrowSvg from "./arrow.svg";

interface Props {
  /** Section ID if the section is named */
  sectionId?: string;
  /** Section number if the section is named */
  sectionNumber?: string;
  /** Selected list item ID */
  selectedAnswerId?: string;
  /** List of answer blocks within this section. */
  answers: AnswerBlockFragment[];
  /** Callback to select a new answer. */
  onSelectAnswer: (answerBlockId: string) => void;
  /** List of answer block IDs that are incomplete. */
  incompleteAnswerIds: string[];
  /**
   * Prevent backtracking or jumping to out-of-order answers.
   * @default false
   */
  disableBacktrack?: boolean;
}

/**
 * Collapsible Section with a question list. The section name is optional.
 */
export function SidebarSection(props: Props) {
  const {
    sectionId,
    sectionNumber,
    answers,
    selectedAnswerId,
    onSelectAnswer,
    incompleteAnswerIds,
    disableBacktrack = false,
  } = props;

  return (
    <div className={styles.container}>
      {sectionNumber != null && sectionId != null && (
        <button
          className={clsx(
            styles.listItem,
            selectedAnswerId === sectionId && styles.selected
          )}
          onClick={() => onSelectAnswer(sectionId)}
        >
          <Text kind="label" color="lilac500">
            Part {sectionNumber}
          </Text>
        </button>
      )}
      {answers.map((block) => {
        let bookmarked = block.bookmark ?? false;
        let skipped = block.skip ?? false;

        // Check if any child questions are skipped
        if (block.question?.questionType === QuestionType.Sub) {
          skipped = answers.some((child) => {
            if (child.parentNodeId === block.nodeId && child.skip === true) {
              return true;
            }
            return false;
          });
          bookmarked = answers.some((child) => {
            if (
              child.parentNodeId === block.nodeId &&
              child.bookmark === true
            ) {
              return true;
            }
            return false;
          });
        }

        return (
          <Item
            key={block.id}
            answerBlockId={block.id}
            orderLabel={block.orderLabel ?? ""}
            bookmarked={bookmarked}
            skipped={skipped}
            selected={selectedAnswerId === block.id}
            answered={!incompleteAnswerIds.includes(block.id)}
            confirmed={block.confirm ?? false}
            isChild={block.orderLabel?.includes(".") ?? false}
            onSelect={() => onSelectAnswer(block.id)}
            questionType={block.question?.questionType}
            shortPrompt={block.question?.shortPrompt ?? undefined}
            disableBacktrack={disableBacktrack}
          />
        );
      })}
    </div>
  );
}

interface ItemProps {
  answerBlockId: string;
  orderLabel: string;
  answered: boolean;
  confirmed: boolean;
  bookmarked: boolean;
  skipped: boolean;
  selected: boolean;
  isChild: boolean;
  onSelect: () => void;
  questionType?: QuestionType;
  shortPrompt?: string;
  disableBacktrack: boolean;
}

const QuestionShortLabels: Partial<Record<QuestionType, string>> = {
  [QuestionType.Blanks]: "Fill in the blanks",
  [QuestionType.Extended]: "Extended response",
  [QuestionType.Hotspot]: "Hotspot",
  [QuestionType.Matching]: "Matching",
  [QuestionType.Mcq]: "Multiple choice",
  [QuestionType.Numerical]: "Numerical",
  [QuestionType.Overview]: "Overview",
  [QuestionType.Section]: "Section",
  [QuestionType.Short]: "Short answer",
  [QuestionType.Sub]: "Sub-question",
  [QuestionType.Truefalse]: "True/false",
  [QuestionType.Video]: "Video response",
};

function Item(props: ItemProps) {
  // Question label
  const label = `Q${props.orderLabel}`;

  let shortPromptEl = null;
  if (props.shortPrompt != null) {
    // Hide the short prompt if backtracking is disabled and this question is
    // not visited in that forward flow
    if (props.disableBacktrack && !props.selected && !props.confirmed) {
      shortPromptEl = <div className={styles.shortSkeleton} />;
    } else {
      shortPromptEl = (
        <Text kind="bodySm" className={styles.shortPrompt}>
          {props.questionType != null &&
          QuestionShortLabels[props.questionType] != null
            ? `${QuestionShortLabels[props.questionType]} - `
            : ""}
          {props.shortPrompt}
        </Text>
      );
    }
  }

  return (
    <button
      className={clsx({
        [styles.questionItem]: true,
        [styles.selected]: props.selected,
        [styles.noCursorPointer]: props.disableBacktrack,
      })}
      onClick={props.disableBacktrack ? undefined : props.onSelect}
      aria-disabled={props.disableBacktrack}
    >
      {props.isChild && (
        <img
          src={arrowSvg}
          style={{ width: 20, height: 20 }}
          alt=""
        />
      )}
      <Text className={styles.label} kind="bodyLine" as="span" bold>
        {label}
      </Text>
      {shortPromptEl}
      <div className={styles.status}>
        <StatusTag
          bookmarked={props.bookmarked}
          answered={props.answered}
          skipped={props.skipped}
          expanded={props.selected}
        />
      </div>
    </button>
  );
}
